export class LineFitter {
    private points: number[][];
    constructor(points: number[][]) {
        this.points = points;
    }

    /**
     * Interpolates a value based on the points provided
     * @param x - The x value to interpolate
     * @returns the interpolated y value
     */
    interpolate(x: number) {
        const result = this.lagrangeInterpolation(x, this.points);
        return isNaN(result) ? 1 : result; // Prevent NaN by defaulting to 1
    }

    /**
     * Inverse interpolate a value based on the points provided
     * @param y - The y value to fit
     * @returns the fitted x value
     */
    invInterpolate(y: number) {
        try {
            return this.newtonRaphson((x) => this.interpolate(x), y);
        } catch {
            return 0;
        }
    }

    private lagrangeBasis(i: number, x: number, points: number[][]): number {
        let basis = 1;
        for (let j = 0; j < points.length; j++) {
            if (j !== i) {
                basis *= (x - points[j][0]) / (points[i][0] - points[j][0]);
            }
        }
        return basis;
    }

    private lagrangeInterpolation(x: number, points: number[][]): number {
        let result = 0;
        for (let i = 0; i < points.length; i++) {
            result += points[i][1] * this.lagrangeBasis(i, x, points);
        }
        return result;
    }

    private derivative(
        f: (x: number) => number,
        x: number,
        h = 0.00001,
    ): number {
        return (f(x + h) - f(x - h)) / (2 * h);
    }

    private newtonRaphson(
        f: (x: number) => number,
        y: number,
        guess = 0,
        tolerance = 1e-7,
        maxIter = 500,
    ): number {
        let x = guess;
        for (let i = 0; i < maxIter; i++) {
            const fx = f(x) - y;
            const fpx = this.derivative(f, x);

            if (Math.abs(fpx) < tolerance) {
                throw new Error('Derivative too small, might not converge.');
            }

            const x1 = x - fx / fpx;

            if (Math.abs(x1 - x) < tolerance) {
                return x1;
            }

            x = x1;
        }
        throw new Error('Newton-Raphson method did not converge.');
    }
}

export class AllocationMultpilerFitter extends LineFitter {
    private numDecimalsMultiplier: number;

    constructor(points: number[][], numDecimalsMultiplier = 0) {
        super(points);

        this.numDecimalsMultiplier = numDecimalsMultiplier;
    }

    percentageToMultiplier(percentage: number): number {
        return this.interpolate(percentage);
    }

    multiplierToPercentage(multiplier: number): number {
        return this.invInterpolate(multiplier);
    }
}
